import { ActivityItem, ChoiceGroup, DefaultButton, DirectionalHint, Dropdown, FontIcon, IActivityItemProps, IChoiceGroupOption, IDropdownOption, IPanelStyles, IPersonaProps, IPersonaSharedProps, IStackTokens, Label, mergeStyles, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, ResponsiveMode, ScrollablePane, ScrollbarVisibility, Spinner, SpinnerSize, Stack, StackItem, TextField, Toggle, TooltipHost } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { getEmailTemplateHtml } from "../../src/taskpane/components/helpers/shareFileEmailTemplateHelper";
import { Fileview } from "../../src/taskpane/components/_v2/components/Explorer/Fileview/Fileview";
import { IGraphCreateShareableLinkRequest, ShareableLinkRequestScope, ShareableLinkRequestType } from "../../src/taskpane/components/_v2/redux/api/graph/api-graph-interfaces";
import { useLazySendMailQuery } from "../../src/taskpane/components/_v2/redux/api/graph/api-graph-slice";
import { useLazyGetSharingInformationQuery } from "../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { PeopleFinder } from "../../src/taskpane/components/_v2/_shared/PeopleFinder/PeopleFinder";
import { ComponentTypes } from "../../src/taskpane/config/config";
import { createItemShareableLink, getFileItemIdFromRelativePath, getItemWebViewUrl } from "../../src/taskpane/services/GraphService";
import { isMobile, mobileListItemStyle } from "../../src/taskpane/styles/mobileStyles";
import authModule from "../../utilities/AuthModule";
import generalModule from "../../utilities/GeneralModule";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { logger } from "../../utilities/logger";
import { useMailSendPopUpAuth } from "../Hooks/useMailSendPopUpAuth";
import { IPageSettings } from "../../src/taskpane/interfaces/IPageSettings";
import { FileIconType, getFileTypeIconAsUrl, IFileTypeIconOptions } from "@fluentui/react-file-type-icons";
import { contentManager, SharePointFolderContentTypeIds } from "../../src/taskpane/config/constants";
import { IAuthAccount } from "../../src/taskpane/interfaces/IAuthTokenResultDto";
import { tryEncodeUriComponent } from "../../utilities/EncodeDecode";

const scrollStyle = mergeStyles({
    position: "relative",
    height: "calc(100vh - 160px)",
});

const stackTokens: IStackTokens = { childrenGap: 20 };

const panelStyles: Partial<IPanelStyles> = {
    content: {
        paddingLeft: 15,
        paddingRight: 15
    }
}

export const enum SharePanelMode {
    Share,
    Copy
}

const minItems = 5;

const navigatorClipboardWriteText = (text: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        try {
            const textArea = document.createElement("textarea");
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            textArea.remove();
            resolve();
        } catch (e) {
            reject(e);
        }
    });
}

export interface ICMFileSharePanelProps {
    panelIsOpen: boolean;
    closePanel: () => void;
    items: any[];
    mode: SharePanelMode;
    isContainer?: boolean;
    hideSharingInfo?: boolean;
    userAccount?: IAuthAccount;
}

export const CMFileSharePanel = ({ panelIsOpen, closePanel, items, mode, isContainer, hideSharingInfo, userAccount }: ICMFileSharePanelProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories?.oneDriveRootDetails);
    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
    const user = useSelector((state: RootState) => state.user);

    const canCreateSharingLink = React.useMemo(() => !items?.some(x => x?.EffectiveBasePermissions?.canEditListItem === false), [items]);

    const [message, setMessage] = React.useState<string>('');
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [filenameWithoutExtention, setFilenameWithoutExtention] = React.useState<string>('');
    const [blockDownload, setBlockDownload] = React.useState(false);
    const [isCreatingLink, setIsCreatingLink] = React.useState(false);
    const [sharedSuccess, setSharedSuccess] = React.useState(false);

    const [types] = React.useState<IDropdownOption[]>([
        { key: 'edit', text: languageStrings.CanEditMakesChanges },
        { key: 'view', text: languageStrings.CanViewCannotMakeChanges }
    ]);
    const [selectedType, setSelectedType] = React.useState<IDropdownOption>(types[0]);

    const [scopes] = React.useState<IChoiceGroupOption[]>([
        { key: ShareableLinkRequestScope.Anonymous, text: languageStrings.Anyone, disabled: true },
        { key: ShareableLinkRequestScope.Organization, text: languageStrings.MyOrganization },
        { key: ShareableLinkRequestScope.Users, text: languageStrings.PeopleYouChoose }
    ]);
    const [selectedScope, setSelectedScope] = React.useState<ShareableLinkRequestScope>(ShareableLinkRequestScope.Users);

    const [activityItem, setActivityItem] = React.useState<IActivityItemProps>();
    const [tooltipContent, setTooltipContent] = React.useState<JSX.Element>();

    const [selectedPeople, setSelectedPeople] = React.useState<string[]>([]);
    const selectedPersonasChanged = (selectedPersonas: IPersonaProps[], customTextValue?: string) => {
        const people = selectedPersonas.map(x => x.secondaryText);
        if (customTextValue) {
            people.push(customTextValue);
        }
        setSelectedPeople(people);
    }

    const [getSharingInformation] = useLazyGetSharingInformationQuery();
    const [sendMail] = useLazySendMailQuery();

    const { isMailSendTokenAvailable, isMailSendTokenRequestInProgress, requestMailSendToken } = useMailSendPopUpAuth();

    const isInOneDrive = React.useRef(currentDirectory?.type == ComponentTypes.OneDrive || currentDirectory?.type == ComponentTypes.OneDriveSubFolder);

    const [showMoreIsClicked, setShowMore] = React.useState<boolean>(false);
    const [sectionStyle, setSectionStyle] = React.useState<any>();
    React.useEffect(() => {
        const itemHeight = isMobile ? mobileListItemStyle.height : 40;
        let containerHeight = itemHeight * items.length;

        if (!showMoreIsClicked) {
            containerHeight = items.length > minItems ? itemHeight * minItems : itemHeight * items.length;
        }

        setSectionStyle(
            mergeStyles({
                maxHeight: containerHeight,
                overflow: "hidden",
                transition: "max-height 0.5s ease-in-out",
            })
        );
    }, [showMoreIsClicked]);

    const showMore = items.length > minItems && (
        <div
            className="xb-show-more-action"
            onClick={() => setShowMore((prevShowMoreIsClicked) => !prevShowMoreIsClicked)}
            style={{ marginTop: "5px" }}
        >
            <FontIcon iconName={!showMoreIsClicked ? "ChevronDown" : "ChevronUp"} />
            <span>{!showMoreIsClicked ? languageStrings.ShowMore : languageStrings.ShowLess}</span>
        </div>
    );

    React.useEffect(() => {
        if (!hideSharingInfo && items?.length === 1) {
            const item = items[0];
            (async () => {
                try {
                    let siteUrl: string;
                    if (item.currentLocation) {
                        const currentLocation: IPageSettings = item.currentLocation;
                        isInOneDrive.current = currentLocation.type == ComponentTypes.OneDrive || currentLocation.type == ComponentTypes.OneDriveSubFolder;
                        siteUrl = isInOneDrive.current ? oneDriveRootDetails?.sharePointSiteUrl : currentLocation.webUrl;
                    }
                    else {
                        siteUrl = isInOneDrive.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl;
                    }

                    const sharingInformation = await getSharingInformation({
                        siteUrl,
                        sharePointListId: item.sharePointListId || item.sharepointIds?.listId || currentSharepointFolder.sharepointListId,
                        uniqueId: item.UniqueId ? item.UniqueId.replace("{", "").replace("}", "") : item.sharepointIds.listItemUniqueId,
                        accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                    }).unwrap();

                    const personas: IPersonaSharedProps[] = sharingInformation?.permissionsInformation?.principals?.map(principal => {
                        let initials = "";

                        const parts = principal.principal.name?.split(" ");

                        if (parts.length === 1) {
                            initials = `${parts[0].charAt(0)}`;
                        }
                        else if (parts.length > 1) {
                            initials = `${parts[0].charAt(0)}${parts[1].charAt(0)}`;
                        }

                        const persona: IPersonaSharedProps = {
                            text: principal.principal.name,
                            imageInitials: initials
                        }
                        return persona;
                    });

                    if (personas?.length > 0) {
                        let activityDescription: any;
                        if (personas.length === 1) {
                            activityDescription = [<span key={1}>{personas[0].text}</span>]
                        }
                        else if (personas.length === 2) {
                            activityDescription = [<span key={1}>{personas[0].text}, {personas[1].text}</span>]
                        }
                        else if (personas.length === 3) {
                            activityDescription = [<span key={1}>{personas[0].text}, {personas[1].text} and 1 other</span>]
                        }
                        else if (personas.length > 3) {
                            activityDescription = [<span key={1}>{personas[0].text}, {personas[1].text} and {personas.length - 2} others</span>]
                        }

                        setTooltipContent(
                            <>
                                <Label>{languageStrings.SharedWith}</Label>
                                <ul>
                                    {personas.map((persona, index) => {
                                        return (
                                            <li key={index}>{persona.text}</li>
                                        )
                                    })}
                                </ul>
                            </>
                        );

                        setActivityItem({
                            activityDescription: activityDescription,
                            activityPersonas: personas,
                            isCompact: true,
                        });
                    }
                } catch (error) {
                    logger(error, "CMFileSharePanel.tsx (SharingInformation)");
                }
            })();
        }
    }, [items]);

    React.useEffect(() => {
        (async () => {
            if (isCreatingLink && isMailSendTokenAvailable) {
                if (selectedScope === ShareableLinkRequestScope.Users && (!selectedPeople || selectedPeople.length === 0)) {
                    setIsCreatingLink(false);
                    setErrorMessage(languageStrings.PleaseSelectSomeoneShare);
                    return;
                }

                setErrorMessage("");

                let hasFailedLinks: boolean = false;
                const links: string[] = [];

                await Promise.all(items?.filter(item => item)?.map(async item => {
                    try {
                        let driveId = '';
                        let itemId = '';

                        if (item.driveId && item.id) {
                            driveId = item.driveId;
                            itemId = item.id;
                        }
                        else if (item.driveId && item.itemId) {
                            driveId = item.driveId;
                            itemId = item.itemId;
                        }
                        else if (item.driveid && item["serverrelativepath"]) { // Advanced search item
                            driveId = item.driveid;
                            itemId = item.itemid ?? await getFileItemIdFromRelativePath(await authModule.getGraphAccessToken(), item.driveid, item["serverrelativepath"]);
                        }
                        else {
                            const spItemUrlparts = item['.spItemUrl'].split('/');
                            driveId = isInOneDrive.current ? oneDriveRootDetails?.driveId : (currentDirectory?.parentId ? currentDirectory?.parentId : currentDirectory?.id);
                            itemId = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];
                        }

                        if (!itemId) {
                            const driveUrl = new URL(await getItemWebViewUrl(await authModule.getGraphAccessToken(), driveId, null));
                            const folderUrl = new URL(driveUrl.origin + item['FileRef']);
                            itemId = await getFileItemIdFromRelativePath(await authModule.getGraphAccessToken(), driveId, folderUrl.pathname.replace(driveUrl.pathname, ""), false);
                        }

                        if (itemId) {
                            try {
                                let shareLink: string;
                                if (canCreateSharingLink) {
                                    const request: IGraphCreateShareableLinkRequest = {
                                        driveId,
                                        itemId,
                                        // recipients: selectedPeople,
                                        message,
                                        type: selectedType.key === ShareableLinkRequestType.Edit
                                            ? ShareableLinkRequestType.Edit
                                            : blockDownload === true
                                                ? ShareableLinkRequestType.BlocksDownload
                                                : ShareableLinkRequestType.View,
                                        scope: selectedScope
                                    }

                                    if (selectedScope === ShareableLinkRequestScope.Users) {
                                        request.recipients = selectedPeople
                                    }

                                    const createLinkResults = await createItemShareableLink(request);
                                    if (createLinkResults?.link?.webUrl) {
                                        shareLink = createLinkResults.link.webUrl;
                                    }
                                }
                                else {
                                    shareLink = await getItemWebViewUrl(await authModule.getGraphAccessToken(), driveId, itemId);
                                }

                                if (shareLink) {
                                    const itemContentTypeId: string = item.ContentTypeId || item.currentLocation?.contentTypeId || item["contenttypeid"];
                                    let isFolderOrDocumentSet: boolean = isContainer === true;
                                    if (!isFolderOrDocumentSet && itemContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder)) {
                                        isFolderOrDocumentSet = true;
                                    }

                                    if (isFolderOrDocumentSet) {
                                        shareLink = `${process.env.contentManagerUrl}?${contentManager.params.preloadURL}=${tryEncodeUriComponent(shareLink)}`;
                                    }
                                    links.push(shareLink);

                                    if (mode === SharePanelMode.Share) {
                                        const fileParts = getFileParts(item.FileLeafRef ?? item.name);
                                        setFilenameWithoutExtention(fileParts.nameWithoutExtention);

                                        let iconUrl: string;
                                        try {
                                            let iconType: IFileTypeIconOptions;
                                            if (itemContentTypeId
                                                && (itemContentTypeId.startsWith(SharePointFolderContentTypeIds.documentSet)
                                                    || itemContentTypeId.startsWith(SharePointFolderContentTypeIds.folder))
                                            ) {
                                                if (itemContentTypeId.startsWith(SharePointFolderContentTypeIds.documentSet)) {
                                                    iconType = { type: FileIconType.docset, size: 48, imageFileType: "svg" };
                                                }
                                                else if (itemContentTypeId.startsWith(SharePointFolderContentTypeIds.folder)) {
                                                    iconType = { type: FileIconType.folder, size: 48, imageFileType: "svg" };
                                                }
                                            }

                                            if (!iconType) {
                                                if (isContainer) {
                                                    iconType = { type: FileIconType.folder, size: 48, imageFileType: "svg" };
                                                }
                                                else {
                                                    iconType = { extension: fileParts.extention?.trim(), size: 48, imageFileType: "svg" };
                                                }
                                            }

                                            iconUrl = getFileTypeIconAsUrl(iconType);
                                        } catch (error) {
                                            iconUrl = `${process.env.baseUri}/assets/icon-unknown.png`;
                                        }

                                        const sharedByText: string = (isFolderOrDocumentSet
                                            ? languageStrings.FolderSharedWithYouBy
                                            : languageStrings.DocumentSharedWithYouBy)
                                            .replace("[person]", user?.account?.displayName ?? userAccount?.displayName);

                                        const res = await sendMail({
                                            accessToken: await authModule.getMailSendAccessToken(),
                                            html: getEmailTemplateHtml(
                                                shareLink,
                                                iconUrl,
                                                sharedByText,
                                                fileParts.nameWithoutExtention,
                                                message,
                                                languageStrings.Open
                                            ),
                                            subject: languageStrings.DocumentSharedWithYouByEmailSubject.replace("[person]", user?.account?.displayName ?? userAccount?.displayName).replace("[file_name]", fileParts.nameWithoutExtention),
                                            toRecipients: selectedPeople
                                        });

                                        if (res.isError) {
                                            hasFailedLinks = true;
                                            setErrorMessage(res.error["data"]["error"]["message"] ? res.error["data"]["error"]["message"] : languageStrings.ErrorMessageGeneric);
                                            logger(res.error["data"]["error"]["message"], "CMFileSharePanel.tsx (sendMail)");
                                        }
                                    }
                                }
                                else {
                                    hasFailedLinks = true;
                                    setErrorMessage(languageStrings.ErrorMessageGeneric);
                                    logger(new Error(`Sharing link could not be created for request ${JSON.stringify({ type: selectedType.key === ShareableLinkRequestType.Edit ? ShareableLinkRequestType.Edit : blockDownload === true ? ShareableLinkRequestType.BlocksDownload : ShareableLinkRequestType.View, scope: selectedScope })}`), "CMFileSharePanel.tsx (getCreateShareableLink)");
                                }
                            } catch (error) {
                                hasFailedLinks = true;
                                setErrorMessage(languageStrings.ErrorMessageGeneric);
                                logger(error, "CMFileSharePanel.tsx (getCreateShareableLink)");
                            }
                        }
                    } catch (error) {
                        hasFailedLinks = true;
                        setErrorMessage(languageStrings.ErrorMessageGeneric);
                        logger(error, "CMFileSharePanel.tsx (items.map -> getCreateShareableLink)");
                    }
                }));

                if (mode === SharePanelMode.Copy && links.length > 0) {
                    try {
                        const textToCopy: string = links.join("\r\n");
                        if (generalModule.getIsOfficeJSAvailable()) {
                            await navigatorClipboardWriteText(textToCopy);
                        }
                        else {
                            await navigator.clipboard.writeText(textToCopy);
                        }
                    } catch (error) {
                        hasFailedLinks = true;
                        setErrorMessage(error?.message ?? languageStrings.ErrorMessageGeneric);
                        logger(error, "CMFileSharePanel.tsx (Clipboard write text)");
                    }
                }

                setSharedSuccess(!hasFailedLinks);
                setIsCreatingLink(false);
            }
            setIsCreatingLink(false);
        })();
    }, [isCreatingLink, isMailSendTokenAvailable])

    const getFileParts = (name: string) => {
        let extention = '';
        let nameWithoutExtention = '';

        const fileParts = name.split('.');
        if (fileParts) {
            if (fileParts.length > 1) extention = fileParts[fileParts.length - 1];
            nameWithoutExtention = fileParts[0];
        }

        return {
            extention,
            nameWithoutExtention
        }
    }

    const createShareableLink = async () => {
        setIsCreatingLink(true);
        setSharedSuccess(false);
        if (!isMailSendTokenAvailable) {
            requestMailSendToken();
        }
    }

    const onRenderFooterContent = React.useCallback(
        () => (
            <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 15 }}>
                {!sharedSuccess && <PrimaryButton
                    iconProps={mode === SharePanelMode.Copy
                        ? { iconName: "Link" }
                        : { iconName: "Send" }}
                    onClick={() => {
                        createShareableLink();
                    }}
                    disabled={isCreatingLink || isMailSendTokenRequestInProgress}
                    text={mode === SharePanelMode.Copy
                        ? languageStrings.CopyLink
                        : languageStrings.Send}
                >
                    {(isCreatingLink) && <Spinner size={SpinnerSize.small} styles={{ root: { paddingLeft: 5 } }} />}
                </PrimaryButton>}
                {sharedSuccess && <DefaultButton
                    iconProps={{ iconName: "Checkmark" }}
                    onClick={() => {
                        closePanel();
                    }}
                    text={languageStrings.Close}
                />}
            </Stack>
        ),
        [createShareableLink, isCreatingLink],
    );

    const onTypeChange = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
        setSelectedType(item);
    };

    const onScopeChange = (ev: React.FormEvent<HTMLInputElement>, option: IChoiceGroupOption): void => {
        setSelectedScope(option.key as ShareableLinkRequestScope);
    }

    return (
        <Panel
            isOpen={panelIsOpen}
            headerText={languageStrings.Share}
            onDismiss={() => closePanel()}
            isLightDismiss
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={!generalModule.isInConentMangerMode() ? "300px" : "450px"}
            styles={panelStyles}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            <div className={scrollStyle} >
                <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                    <Stack tokens={stackTokens} style={{ marginTop: '15px' }}>
                        {errorMessage && <MessageBar messageBarType={MessageBarType.error} isMultiline={true} dismissButtonAriaLabel="Close" onDismiss={() => setErrorMessage("")}>
                            {errorMessage}
                        </MessageBar>}
                        {sharedSuccess && <MessageBar messageBarType={MessageBarType.success} isMultiline={false} dismissButtonAriaLabel="Close">
                            {mode === SharePanelMode.Share
                                ? languageStrings.DocumentShareSuccess.replace("[file_name]", filenameWithoutExtention)
                                : `${items.length} ${languageStrings.SharedLinkSuccessfullyCopied}`}
                        </MessageBar>}
                        <div className={sectionStyle}>
                            {items?.map((item, index) => (
                                <div key={index}>
                                    <Fileview
                                        item={item}
                                        isMobile={isMobile}
                                        multiSelect={false}
                                        hasShowMore={false}
                                        tenantEnablePII={false}
                                        nonClickable={true}
                                    />
                                </div>
                            ))}
                        </div>
                        {showMore}
                        {mode === SharePanelMode.Copy && <ChoiceGroup label={languageStrings.ShareLinkWith} defaultSelectedKey={selectedScope} options={scopes} onChange={onScopeChange} />}
                        {selectedScope === ShareableLinkRequestScope.Users && <PeopleFinder label={languageStrings.ShareWithPeople} selectedPersonasChanged={selectedPersonasChanged} />}
                        {mode === SharePanelMode.Share && <TextField label={languageStrings.Message} value={message} multiline rows={5} onChange={(event, newValue) => setMessage(newValue)} />}
                        {canCreateSharingLink && <Dropdown
                            label={languageStrings.Type}
                            options={types}
                            selectedKey={selectedType.key}
                            onChange={onTypeChange}
                        />}
                        {canCreateSharingLink && selectedType.key === ShareableLinkRequestType.View && <Toggle label={languageStrings.BlockDownload} checked={blockDownload} onText={languageStrings.Yes} offText={languageStrings.No} onChange={() => {
                            setBlockDownload(prev => !prev);
                        }} />}
                        {activityItem && <StackItem>
                            <Label>{languageStrings.SharedWith}</Label>
                            <TooltipHost content={tooltipContent} directionalHint={DirectionalHint.topLeftEdge}>
                                <ActivityItem {...(activityItem as IActivityItemProps)} />
                            </TooltipHost>
                        </StackItem>}
                    </Stack>
                </ScrollablePane>
            </div>
        </Panel>
    )
};